/**
 * NPSModal · v8.5.64 · piel — caja de 460px, pregunta en font-display 24px,
 * escala 0-10 como diez cuadrados de borde 1px (el elegido en --ink),
 * campo opcional de 15px, primaria "Enviar", "Ahora no" fantasma.
 *
 * Condición nueva: solo se pide turno si el cliente ya tomó una decisión
 * esta sesión (`huboDecisionEstaSesion()`, marcada por DecisionBar.jsx al
 * aprobar) — antes solo dependía del delay de 8s + `api.npsPendiente`.
 * Un cliente que recién entra y todavía no decidió nada no debería
 * encontrarse con una encuesta antes de haber hecho algo en el portal.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useToast } from "./Toast.jsx";
import { useInterruptSlot, huboDecisionEstaSesion } from "../context/InterruptBudget.jsx";

const DISMISS_KEY = "nps_dismissed_at";
const DISMISS_TTL = 7 * 24 * 60 * 60 * 1000; // 7 días

export default function NPSModal() {
  const { clienteActivoId } = useAuth();
  const { toast } = useToast();
  const [pendiente, setPendiente] = useState(false);
  const [score, setScore] = useState(null);
  const [comentario, setComentario] = useState("");
  const [enviando, setEnviando] = useState(false);
  // v8.5.27 · F4 · presupuesto de interrupción: prioridad más baja de las 3
  //   (onboarding y mood importan más en el momento).
  const { granted, request, release } = useInterruptSlot("nps", 1);

  useEffect(() => {
    if (!clienteActivoId) return;

    const dismissed = parseInt(localStorage.getItem(`${DISMISS_KEY}_${clienteActivoId}`) || "0", 10);
    if (dismissed && (Date.now() - dismissed) < DISMISS_TTL) return;

    // Delay para no ser intrusivo: 8 seg después de entrar, y solo si ya
    // decidió algo (aprobó una pieza) — sino esperamos a que lo haga.
    const t = setTimeout(async () => {
      if (!huboDecisionEstaSesion()) return;
      try {
        const r = await api.npsPendiente(clienteActivoId);
        if (r.pendiente) { setPendiente(true); request(); }
      } catch {}
    }, 8000);
    return () => clearTimeout(t);
  }, [clienteActivoId]);

  const cerrar = () => {
    localStorage.setItem(`${DISMISS_KEY}_${clienteActivoId}`, String(Date.now()));
    setPendiente(false);
    release();
  };

  const enviar = async () => {
    if (score == null) return;
    setEnviando(true);
    try {
      await api.npsResponder(clienteActivoId, { score, comentario });
      toast.success("¡Gracias por tu feedback!");
      cerrar();
    } catch (err) { toast.error(err.message); }
    finally { setEnviando(false); }
  };

  if (!pendiente || !granted) return null;

  return (
    <div className="fixed inset-0 bg-[var(--ink)]/40 backdrop-blur-sm z-[var(--z-modal)] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="shadow-overlay bg-[var(--raised)] w-full sm:max-w-[460px] rounded-t-[12px] sm:rounded-[20px]">
        <div className="p-5">
          <h2 className="font-display text-[24px] leading-snug text-[var(--ink)]">
            ¿Qué tan probable es que recomiendes a The Crew?
          </h2>

          {/* v8.5.69 · fix auditoría móvil · 11 casillas flex-1 en 375px daban
              ~27px cada una — bajo el mínimo de tap. En mobile son 40px fijos
              en una fila con scroll horizontal; en sm: vuelve al reparto
              parejo de antes (sí entran cómodas). */}
          <div className="flex gap-1.5 mt-5 overflow-x-auto scrollbar-hide sm:overflow-visible sm:justify-between">
            {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
              <button key={n} onClick={() => setScore(n)}
                      className={`w-10 h-10 flex-shrink-0 sm:flex-1 sm:w-auto sm:aspect-square rounded-[12px] border font-mono text-[13px] font-medium transition-colors ${
                        score === n
                          ? "bg-[var(--ink)] text-[var(--surface)] border-[var(--ink)]"
                          : "border-[var(--line-strong)] text-[var(--ink-3)] hover:border-[var(--ink)]"
                      }`}>
                {n}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-[13px] text-[var(--faint)] mt-1.5">
            <span>Nada probable</span>
            <span>Muy probable</span>
          </div>

          {score != null && (
            <textarea value={comentario} onChange={e => setComentario(e.target.value)} rows={3}
                      placeholder={
                        score >= 9 ? "¿Qué fue lo mejor? (opcional)" :
                        score >= 7 ? "¿Qué podríamos mejorar? (opcional)" :
                        "Contanos qué pasó — nos importa mucho"
                      }
                      className="w-full mt-4 p-3 bg-[var(--surface)] border border-[var(--line-control)] rounded-[14px] text-[15px] focus:outline-none focus:border-[var(--accent)] resize-none text-[var(--ink)] placeholder:text-[var(--faint)]"/>
          )}
        </div>

        <div className="p-4 border-t border-[var(--line)] flex items-center justify-end gap-3">
          <button onClick={cerrar} className="text-[13px] text-[var(--accent)] hover:underline">Ahora no</button>
          <button onClick={enviar} disabled={score == null || enviando}
                  className="bg-[var(--ink)] text-[var(--surface)] px-5 py-2 rounded-full text-sm font-medium disabled:opacity-40 hover:opacity-90">
            {enviando ? "Enviando…" : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
}
